import { useEffect, useRef } from 'react';

const BASE_CHARS = 'アイウエオカキクケコサシスセソタチツテトナニヌネノハヒフヘホ';
const SPECIAL_CHARS = 'Braux206'.repeat(24);
const DRAW_CHARS = `${BASE_CHARS}${SPECIAL_CHARS}`;
const STREAM_SEQUENCE = ['Braux', '2026'];
const ORDERED_STREAM_COUNT = 12;
const ANIMATION_SPEED_PERCENT = 110;
const DRAW_INTERVAL_MS = ANIMATION_SPEED_PERCENT;
const FONT_SIZE = 20;

function getCanvasMetrics(canvas, context) {
  const dpr = window.devicePixelRatio || 1;
  const width = window.innerWidth;
  const height = window.innerHeight;
  canvas.width = width * dpr;
  canvas.height = height * dpr;
  canvas.style.width = `${width}px`;
  canvas.style.height = `${height}px`;
  context.setTransform(dpr, 0, 0, dpr, 0, 0);
  return { width, height, columnCount: Math.floor(width / FONT_SIZE) };
}

function buildOrderedStreamColumns(columnCount) {
  return Array.from({ length: ORDERED_STREAM_COUNT }, (_, index) => ({
    stream: STREAM_SEQUENCE[index % STREAM_SEQUENCE.length],
    column: Math.max(2 + index * 2, Math.floor(columnCount * ((index + 1) / (ORDERED_STREAM_COUNT + 1)))),
  })).filter((item, index, array) => item.column < columnCount && array.findIndex((candidate) => candidate.column === item.column) === index);
}

function createDropState(columnCount) {
  return Array.from({ length: columnCount }, () => Math.floor(Math.random() * -50));
}

function getDrawCharacter(streamColumn, drop) {
  if (streamColumn && drop * FONT_SIZE > 0) {
    const streamIndex = ((drop % streamColumn.stream.length) + streamColumn.stream.length) % streamColumn.stream.length;
    return {
      character: streamColumn.stream[streamIndex],
      color: '#a9ffbc',
    };
  }

  return {
    character: DRAW_CHARS[Math.floor(Math.random() * DRAW_CHARS.length)],
    color: '#26d07c',
  };
}

function drawFrame(context, drops, streamColumns) {
  const width = window.innerWidth;
  const height = window.innerHeight;
  context.fillStyle = 'rgba(0, 0, 0, 0.06)';
  context.fillRect(0, 0, width, height);
  context.font = `${FONT_SIZE}px monospace`;

  drops.forEach((drop, index) => {
    const x = index * FONT_SIZE;
    const y = drop * FONT_SIZE;
    const streamColumn = streamColumns.find((item) => item.column === index);
    const { character, color } = getDrawCharacter(streamColumn, drop);

    context.fillStyle = color;
    context.fillText(character, x, y);
    drops[index] = y > height && Math.random() > 0.975 ? 0 : drop + 1;
  });
}

export default function MatrixRainBackground() {
  const canvasRef = useRef(null);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const context = canvas.getContext('2d');
    let animationFrameId = 0;
    let lastDraw = 0;
    let drops = [];
    let streamColumns = [];

    const resize = () => {
      const { columnCount } = getCanvasMetrics(canvas, context);
      drops = createDropState(columnCount);
      streamColumns = buildOrderedStreamColumns(columnCount);
    };

    const draw = (timestamp) => {
      if (timestamp - lastDraw >= DRAW_INTERVAL_MS) {
        drawFrame(context, drops, streamColumns);
        lastDraw = timestamp;
      }

      animationFrameId = window.requestAnimationFrame(draw);
    };

    resize();
    window.addEventListener('resize', resize);
    animationFrameId = window.requestAnimationFrame(draw);

    return () => {
      window.removeEventListener('resize', resize);
      window.cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <>
      <canvas ref={canvasRef} className="matrix-rain-canvas" aria-hidden="true" data-testid="login-matrix-canvas" />
      <div className="login-matrix-overlay" aria-hidden="true" data-testid="login-matrix-overlay" />
      <div className="login-matrix-bottom-fade" aria-hidden="true" />
    </>
  );
}
